import { EasyfyEventData, IDataMapper, IEasyfyEvent, IPageViewData, IQuantityChangedData, ISearchPerformedData, ISessionStarted } from "@shared/easyfy.events/easyfy.eventdata";
import { EventNames } from "@shared/easyfy.events/easyfy.eventhandler";
import { EasyfyEventLogger } from "@shared/easyfy.events/easyfy.event.devlogger";
import { event } from "../../easyfy.decorators";

/* Exporter class */
export class StatsDataMapper implements IDataMapper {

  MapEventData(event: IEasyfyEvent): any {
    var mappedEvent;

    switch (event.eventName.toLowerCase()) {
      case EventNames.SessionStarted: {
        mappedEvent = this.BuildSessionStartedEvent(event);
        break;
      }
      case EventNames.PageView: {
        mappedEvent = this.BuildPageViewEvent(event);
        break;
      }
      case EventNames.ProductPageView: {
        mappedEvent = this.BuildProductPageViewEvent(event);
        break;
      }
      case EventNames.SearchPerformed: {
        mappedEvent = this.BuildSearchPerformedEvent(event);
        break;
      }
      case EventNames.AddToCart: {
        if (event.data.isFirstAddForRow) {
          mappedEvent = this.BuildAddToBasketEvent(event);
        }
        else {
          EasyfyEventLogger.Log({ type: "warning", message: `Event '${event.eventName}' skipped for stats, conditions not met` });
        }
        break;
      }
      case EventNames.RemoveFromCart: {
        if (event.data.isLastRemoveForRow) {
          mappedEvent = this.BuildRemoveFromBasketEvent(event);
        }
        else {
          EasyfyEventLogger.Log({ type: "warning", message: `Event '${event.eventName}' skipped for stats, conditions not met` });
        }
        break;
      }
      case EventNames.QuantityChanged: {
        mappedEvent = this.BuildQuantityChangedEvent(event);
        break;
      }

      case EventNames.ProductImpression: {
        mappedEvent = this.BuildProductImpressionEvents(event);
        break;
      }
    }

    return mappedEvent;
  }

  BuildSessionStartedEvent(event: IEasyfyEvent): any {
    var data = event.data as ISessionStarted;
    return {
      type: "SessionStarted",
      TimeStampUtc: this.GetFormattedTimeStamp(),
      Culture: data.Culture,
      Currency: data.Currency,
      IsDesktop: data.IsDesktop,
      IsMobilePhone: data.IsMobilePhone,
      IsTablet: data.IsTablet,
      RecurringUser: data.RecurringUser,
      Referrer: data.Referrer,
      Path: data.Path,
      UserAgent: data.UserAgent
    };
  }

  BuildPageViewEvent(event: IEasyfyEvent): any {
    var data = event.data as IPageViewData;

    return {
      type: "PageViewed",
      Path: this.GetCurrentUrlPath(),
      PageId: data.pageId,
      TimeStampUtc: this.GetFormattedTimeStamp(),
    };
  }

  BuildProductPageViewEvent(event: IEasyfyEvent): any {
    if (!event.data.productIdentifier) throw new Error('Product identifier missing');
    var product = EasyfyEventData.Product(event.data.productIdentifier);

    return {
      type: "ProductViewed",
      ProductId: product.DomainId,
      Path: this.GetCurrentUrlPath(),
      Referrer: document.referrer,
      TimeStampUtc: this.GetFormattedTimeStamp(),
    };
  }

  BuildSearchPerformedEvent(event: IEasyfyEvent): any {
    var data = event.data as ISearchPerformedData;

    return {
      Path: this.GetCurrentUrlPath(),
      SearchWord: data.searchString,
      TimeStampUtc: this.GetFormattedTimeStamp(),
      type: "UserSearched",
    };
  }

  BuildAddToBasketEvent(event: IEasyfyEvent): any {
    if (!event.data.productIdentifier) throw new Error('Product identifier missing');

    var product = EasyfyEventData.Product(event.data.productIdentifier);
    var salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(event.data.productIdentifier + "-" + event.data.salesUnitCode);
    var productListIndex = EasyfyEventData.ProductIndexInList(event.data.listName, product.Id) + 1;
    var pageInfo = EasyfyEventData.PageInfo();

    return {
      type: "ProductAddedToBasket",
      Path: this.GetCurrentUrlPath(),
      TimeStampUtc: this.GetFormattedTimeStamp(),
      ProductId: product.DomainId,
      Quantity: event.data.quantity,
      LineNumber: event.data.rowId,
      ListPosition: productListIndex,
      ListType: event.data.listName,
      Origin: pageInfo.PageType,
      View: pageInfo.ViewName
    };
  }

  BuildRemoveFromBasketEvent(event: IEasyfyEvent): any {
    var product = EasyfyEventData.Product(event.data.productIdentifier);
    var salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(event.data.productIdentifier + "-" + event.data.salesUnitCode);

    return {
      type: "ProductRemovedFromBasket",
      Path: this.GetCurrentUrlPath(),
      TimeStampUtc: this.GetFormattedTimeStamp(),
      Sku: product.Id,
      LineNumber: event.data.rowId
    };
  }

  BuildQuantityChangedEvent(event: IEasyfyEvent): any {
    var data = event.data as IQuantityChangedData;
    var product = EasyfyEventData.Product(event.data.productIdentifier);
    var salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(event.data.productIdentifier + "-" + event.data.salesUnitCode);

    return {
      type: "ProductQuantityChanged",
      TimeStampUtc: this.GetFormattedTimeStamp(),
      NewQuantity: data.newQuantity,
      OldQuantity: data.oldQuantity,
      Sku: product.Id,
      LineNumber: event.data.rowId,
      Url: this.GetCurrentUrlPath()
    };
  }

  BuildProductImpressionEvents(event: IEasyfyEvent): any[] {
    if (!event.data.productIdentifiers) throw new Error('Product identifiers missing');
    if (!event.data.salesUnitCodes) throw new Error('Product sales unit codes missing');
    if (event.data.salesUnitCodes.length != event.data.productIdentifiers.length) throw new Error('Product sales unit codes and identifiers count missmatch');
    var priceIds: string[] = [];
    var siteInfo = EasyfyEventData.SiteInfo();
    var productImpressions: any[] = [];

    for (var i = 0; i < event.data.productIdentifiers.length; i++) {
      priceIds.push(event.data.productIdentifiers[i] + "-" + event.data.salesUnitCodes[i]);
    }

    var eventProducts = EasyfyEventData.Products(event.data.productIdentifiers);
    var eventPrices = EasyfyEventData.ProductSalesUnitPrices(priceIds);

    for (var i = 0; i < eventProducts.length; i++) {
      var product = eventProducts[i];
      var salesUnitPrice = eventPrices[i];

      var index = eventProducts.indexOf(product);
      var productListIndex = EasyfyEventData.ProductIndexInList(event.data.listIds[index], product.Id) + 1;

      productImpressions.push({
        type: "ProductImpression",
        TimeStampUtc: this.GetFormattedTimeStamp(),
        ListPosition: productListIndex,
        ListType: event.data.listIds[index],
        Path: this.GetCurrentUrlPath(),
        ProductId: product.DomainId,
      });
    }

    return productImpressions;
  }

  GetCurrentUrlPath(): string {
    return window.location.pathname;
  }

  GetFormattedTimeStamp(): string {
    var date = (new Date).toISOString();
    return date;
  }
}